import { type JSX, useState } from "react";
import { useTranslation } from "react-i18next";

import BugReportIcon from "@mui/icons-material/BugReport";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import {
  ConfigurationMessages,
  DebugOptionsSelector,
  DebugSpeedDial,
  DebugSpeedDialAction,
  LocalizationProvider,
} from "@plansoft-configuration-renderer/components";
import {
  ConfigurationController,
  ConfigurationInitializer,
  ConfigurationRenderer,
  HeartbeatController,
} from "@plansoft-configuration-renderer/framework";
import { SettingsSync } from "@plansoft-configuration-renderer/layout";
import { TranslationNamespace } from "@plansoft-configuration-renderer/localization";

import { ConfigurationStateSpy } from "./ConfigurationStateSpy";
import { CustomRenderer } from "./CustomRenderer";
import "./MinimalRenderer.css";

export interface MinimalRendererProps {
  /**
   * Configuration object id
   */
  cfgObjectId: string;
  /**
   * Configuration language / locale code
   */
  language: string;
  /**
   * The base URL must be mapped by the proxy
   */
  baseUrl: string;
  /**
   * Use a specific layout, only works for the root cfg object
   */
  layoutId?: string;
  sessionId?: string;
}

const isDevEnvironment =
  window.location.href.includes("stag") ||
  window.location.href.includes("dev") ||
  window.location.href.includes("localhost");

const UI_CONTEXTS = [
  TranslationNamespace.ConfigurationRenderer,
  TranslationNamespace.CustomApp,
];

/**
 * This is the base setup for the renderer
 *
 * - the `CsCfgObjectController` starts the configuration
 * - the `ConfigurationController` provides the necessary state management
 */
export function MinimalRenderer(props: MinimalRendererProps): JSX.Element {
  // see setupProxy.js in src folder
  // in production this could be loaded from the database et
  const { baseUrl, ...startOptions } = props;

  // if any start option changes, reset the ConfigurationController
  const configurationResetKey = Object.values(props).join("#");

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const sessionStorageId = window.sessionStorage.getItem("cfgSessionId");

  return (
    <LocalizationProvider
      configurationLocale={startOptions.language}
      uiLocale={startOptions.language}
      uiContexts={UI_CONTEXTS}
      configuratorBasePath={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
    >
      <ConfigurationController baseUrl={baseUrl} key={configurationResetKey}>
        {/* ConfigurationStarter starts to configuration but renders nothing. Also see ConfigurationResumer */}
        {startOptions.sessionId ? (
          <ConfigurationInitializer
            sessionId={startOptions.sessionId}
            localeId={startOptions.language}
          />
        ) : sessionStorageId ? (
          <ConfigurationInitializer
            sessionId={sessionStorageId}
            localeId={startOptions.language}
          />
        ) : (
          <ConfigurationInitializer
            cfgObjectId={startOptions.cfgObjectId}
            localeId={startOptions.language}
          />
        )}
        {/* ConfigurationRenderer prevents the configuration from rendering when there is not cfg object yet */}
        <ConfigurationRenderer>
          {/* Renders nothing, but keeps the session alive */}
          <HeartbeatController interval={60e3} />
          {/* Sync settings to redux store, omit if not needed */}
          <SettingsSync />
          {/* Renders the configuration */}
          <CustomRenderer />
          {/* Spy on configuration state changes */}
          {isDevEnvironment && (
            <Box position="fixed" right="20px" bottom="20px" zIndex={99999}>
              <DebugSpeedDial
                icon={<BugReportIcon />}
                defaultIsOpen={false}
                FabProps={{
                  sx: {
                    backgroundColor: "warning.main",
                  },
                }}
              >
                <DebugSpeedDialAction
                  icon={<BugReportIcon />}
                  tooltipTitle={t("regiolux:showDebugDrawer")}
                  onClick={() => setIsOpen(!isOpen)}
                />
              </DebugSpeedDial>
            </Box>
          )}
          <Drawer
            variant="temporary"
            anchor="right"
            open={isOpen}
            onClose={() => setIsOpen(false)}
          >
            <DebugOptionsSelector />
          </Drawer>
          <ConfigurationMessages />
          <ConfigurationStateSpy />
        </ConfigurationRenderer>
      </ConfigurationController>
    </LocalizationProvider>
  );
}
